import type { CategoriesApi } from "@/v2/api/metadata/categories";
import type { SportsApi } from "@/v2/api/metadata/sports";
import type { TeamsApi } from "@/v2/api/metadata/teams";
import type { TournamentsApi } from "@/v2/api/metadata/tournaments";
import type { ContentTab, DialogTabSaveContext } from "@/v2/framework/dialog/types";
import { mergeEntityPatches } from "@/v2/framework/utils/mergeEntityPatches";
import {
  buildSupplementalNamesPatch,
  syncSupplementalNamesBaseline,
} from "@/v2/kits/supplemental-names";
import { createInheritableTabSave } from "@/v2/kits/tenant-inheritance";
import { teamStatusOptions } from "../../../../shared";
import { buildTournamentTeamsPatch, syncTournamentTeamsBaseline } from "../../tournaments/patch";
import { notifySuccess, teamId } from "../shared";

const mainTabChildren: ContentTab["children"] = [
  {
    type: "select-field",
    name: "status",
    label: "Status",
    required: true,
    placeholder: "Select status",
    options: teamStatusOptions,
    inheritance: { computedKey: "computedStatus", parentKey: "parentStatus" },
  },
  {
    type: "text-field",
    name: "name",
    label: "Name",
    placeholder: "Enter name",
    inheritance: { computedKey: "computedName", parentKey: "parentName" },
    validate: (value, values) => {
      if (values.isTenantSourceOfEntity && !String(value ?? "").trim()) return "Required";
      return undefined;
    },
  },
  {
    type: "text-field",
    name: "info",
    label: "Info",
    placeholder: "Enter info",
    inheritance: { computedKey: "computedInfo", parentKey: "parentInfo" },
  },
];

export function createMainTab(options: {
  teamsApi: TeamsApi;
  sportsApi: SportsApi;
  categoriesApi: CategoriesApi;
  tournamentsApi: TournamentsApi;
}): ContentTab {
  const { teamsApi, sportsApi, categoriesApi, tournamentsApi } = options;

  const save = createInheritableTabSave({
    children: mainTabChildren,
    buildExtraPatch: (ctx) => {
      const supplemental = buildSupplementalNamesPatch(ctx.values, ctx.baseline);
      const tournaments = buildTournamentTeamsPatch(ctx.values, ctx.baseline);
      const merged = supplemental ? mergeEntityPatches(supplemental, tournaments ?? {}) : tournaments;
      return merged && Object.keys(merged).length ? merged : null;
    },
    afterPersist: (ctx) => {
      syncSupplementalNamesBaseline(ctx);
      syncTournamentTeamsBaseline(ctx.values, ctx.baseline);
    },
    persist: async (patch, ctx: DialogTabSaveContext) => {
      const { tournamentTeams, ...entityPatch } = patch;
      const id = teamId(ctx.values);
      if (Object.keys(entityPatch).length) await teamsApi.update(id, entityPatch);
      if (tournamentTeams) await teamsApi.updateTournaments(id, { tournamentTeams });
      notifySuccess();
    },
  });

  return {
    id: "main",
    label: "Main",
    save,
    children: [
      ...mainTabChildren,
      {
        type: "component",
        component: () =>
          import("@/v2/kits/supplemental-names/components/SupplementalNamesFields.vue"),
        props: { mode: "edit", entity: "TEAM" },
      },
      {
        type: "component",
        component: () => import("../../tournaments/TeamTournamentsFields.vue"),
        props: { mode: "edit", sportsApi, categoriesApi, tournamentsApi },
      },
    ],
  };
}
